import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Box, type BoxProps } from '@mui/material';

import { useReducedMotion } from '@/hooks/useReducedMotion';

export const STUDENT_LOADING_DELAY_MS = 180;
export const STUDENT_SKELETON_FADE_MS = 180;
export const STUDENT_CONTENT_REVEAL_MS = 300;
export const STUDENT_LONG_WAIT_MS = 8_000;

export type StudentContentState = 'pending' | 'ready' | 'error';

interface LoadingCycleScopeValue {
  cycleKey: string;
  startedAt: number;
  resetKey: string;
}

const LoadingCycleScopeContext = createContext<LoadingCycleScopeValue | null>(
  null
);

interface LoadingCycleRegistration {
  cycleKey: string;
  participantId: string;
  label: string;
  startedAt: number;
}

interface LoadingCycleRecord {
  cycleKey: string;
  label: string;
  startedAt: number;
  participants: Set<string>;
  releaseTimer: number | null;
}

interface LoadingCycleRegistryValue {
  register: (registration: LoadingCycleRegistration) => void;
  unregister: (cycleKey: string, participantId: string) => void;
}

const LoadingCycleRegistryContext =
  createContext<LoadingCycleRegistryValue | null>(null);

interface StudentLoadingSlotContextValue {
  nested: boolean;
  startedAt: number | null;
  takingLonger: boolean;
}

const StudentLoadingSlotContext = createContext<StudentLoadingSlotContextValue>(
  {
    nested: false,
    startedAt: null,
    takingLonger: false,
  }
);

/**
 * Lets skeleton compositions defer their own announcement/timer when a
 * persistent content boundary already owns the loading cycle.
 */
export const useStudentLoadingSlot = (): StudentLoadingSlotContextValue =>
  useContext(StudentLoadingSlotContext);

const visuallyHiddenStyle: React.CSSProperties = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};

/**
 * Stable announcement owner for loading stages that hand off across mounts
 * (route chunk -> auth -> data, for example). The first label survives until
 * the final participant leaves, so screen readers hear one cycle rather than
 * each implementation stage.
 */
export const StudentLoadingCycleProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const recordsRef = useRef(new Map<string, LoadingCycleRecord>());
  const [, refresh] = useReducer((version: number) => version + 1, 0);

  const register = useCallback((registration: LoadingCycleRegistration) => {
    const existing = recordsRef.current.get(registration.cycleKey);
    if (existing) {
      if (existing.releaseTimer !== null) {
        window.clearTimeout(existing.releaseTimer);
        existing.releaseTimer = null;
      }
      existing.startedAt = Math.min(existing.startedAt, registration.startedAt);
      existing.participants.add(registration.participantId);
    } else {
      recordsRef.current.set(registration.cycleKey, {
        cycleKey: registration.cycleKey,
        label: registration.label,
        startedAt: registration.startedAt,
        participants: new Set([registration.participantId]),
        releaseTimer: null,
      });
    }
    refresh();
  }, []);

  const unregister = useCallback((cycleKey: string, participantId: string) => {
    const record = recordsRef.current.get(cycleKey);
    if (!record) return;
    record.participants.delete(participantId);
    if (record.participants.size > 0 || record.releaseTimer !== null) {
      refresh();
      return;
    }

    // A zero-delay hand-off lets a replacement stage register during the
    // same commit without tearing down and re-announcing the shared cycle.
    record.releaseTimer = window.setTimeout(() => {
      const current = recordsRef.current.get(cycleKey);
      if (!current || current.participants.size > 0) return;
      recordsRef.current.delete(cycleKey);
      refresh();
    }, 0);
    refresh();
  }, []);

  useEffect(
    () => () => {
      for (const record of recordsRef.current.values()) {
        if (record.releaseTimer !== null) {
          window.clearTimeout(record.releaseTimer);
        }
      }
      recordsRef.current.clear();
    },
    []
  );

  const registry = useMemo(
    () => ({ register, unregister }),
    [register, unregister]
  );
  const activeRecords = Array.from(recordsRef.current.values()).filter(
    record => record.participants.size > 0
  );

  return (
    <LoadingCycleRegistryContext.Provider value={registry}>
      {children}
      {activeRecords.map(record => (
        <span
          key={record.cycleKey}
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label={record.label}
          data-student-loading-cycle={record.cycleKey}
          style={visuallyHiddenStyle}
        >
          {record.label}
        </span>
      ))}
    </LoadingCycleRegistryContext.Provider>
  );
};

export interface StudentLoadingCycleScopeProps {
  cycleKey: string;
  startedAt: number;
  /** Route/account identity used to discard retained presentation safely. */
  resetKey?: string;
  children: React.ReactNode;
}

/** Supplies one clock to consecutive boundaries without remounting children. */
export const StudentLoadingCycleScope: React.FC<
  StudentLoadingCycleScopeProps
> = ({ cycleKey, startedAt, resetKey = cycleKey, children }) => {
  const value = useMemo(
    () => ({ cycleKey, startedAt, resetKey }),
    [cycleKey, startedAt, resetKey]
  );
  return (
    <LoadingCycleScopeContext.Provider value={value}>
      {children}
    </LoadingCycleScopeContext.Provider>
  );
};

interface PresentationState {
  mode: 'ready' | 'pending' | 'revealing' | 'error';
  skeletonMounted: boolean;
  skeletonOpacity: number;
  previousOpacity: number;
  incomingOpacity: number;
  transitionMs: number;
}

const readyPresentation: PresentationState = {
  mode: 'ready',
  skeletonMounted: false,
  skeletonOpacity: 0,
  previousOpacity: 0,
  incomingOpacity: 1,
  transitionMs: 0,
};

const errorPresentation: PresentationState = {
  ...readyPresentation,
  mode: 'error',
};

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.min(maximum, Math.max(minimum, value));

const measuredHeight = (node: HTMLElement | null): number | null => {
  if (!node) return null;
  const rectHeight = node.getBoundingClientRect().height;
  const height = Math.max(rectHeight, node.scrollHeight);
  return height > 0 ? height : null;
};

const blockRetainedInteraction = (event: React.SyntheticEvent): void => {
  event.preventDefault();
  event.stopPropagation();
};

const rejectRetainedFocus = (event: React.FocusEvent): void => {
  event.preventDefault();
  event.stopPropagation();
  (event.target as HTMLElement).blur();
};

export interface StudentContentBoundaryProps
  extends Omit<BoxProps, 'children' | 'content'> {
  state: StudentContentState;
  /** Stable identity of the destination data, not of the loading request. */
  contentKey: string | number;
  label: string;
  skeleton: React.ReactNode;
  children: React.ReactNode;
  /** Blocking recovery content. It is shown immediately, without a fade. */
  error?: React.ReactNode;
  /** Keep only the last ready presentation during the 180ms grace window. */
  retainPrevious?: boolean;
  /**
   * Existing question/map/recap choreography can keep its own entrance. The
   * outgoing placeholder still dissolves, but the ready content does not gain
   * a second opacity animation.
   */
  animateContent?: boolean;
  cycleKey?: string;
  cycleStartedAt?: number;
  /** Explicit route/account reset. Nearest cycle scope is the default. */
  resetKey?: string;
  loadingDelayMs?: number;
  skeletonFadeMs?: number;
  revealMs?: number;
  longWaitMs?: number;
}

/**
 * Persistent presentation boundary for student data.
 *
 * Query state stays outside this component. It receives explicit
 * pending/ready/error truth, then owns only presentation: a grace period,
 * inert retained content, a destination-shaped skeleton, interruptible
 * dissolve, and the temporary height constraint between presentations.
 */
export const StudentContentBoundary: React.FC<StudentContentBoundaryProps> = ({
  state,
  contentKey,
  label,
  skeleton,
  children,
  error = null,
  retainPrevious = false,
  animateContent = true,
  cycleKey: explicitCycleKey,
  cycleStartedAt,
  resetKey: explicitResetKey,
  loadingDelayMs = STUDENT_LOADING_DELAY_MS,
  skeletonFadeMs = STUDENT_SKELETON_FADE_MS,
  revealMs = STUDENT_CONTENT_REVEAL_MS,
  longWaitMs = STUDENT_LONG_WAIT_MS,
  sx,
  ...boxProps
}) => {
  const reducedMotion = useReducedMotion();
  const parentSlot = useContext(StudentLoadingSlotContext);
  const scope = useContext(LoadingCycleScopeContext);
  const registry = useContext(LoadingCycleRegistryContext);
  const participantId = useId();
  const localCycleKey = `student-loading:${participantId}`;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const incomingRef = useRef<HTMLDivElement | null>(null);
  const lastReadyHeightRef = useRef<number | null>(null);
  const lastReadyContentRef = useRef<React.ReactNode>(
    state === 'ready' ? children : null
  );
  const lastReadyIdentityRef = useRef<string | number | null>(
    state === 'ready' ? contentKey : null
  );
  const outgoingContentRef = useRef<React.ReactNode>(null);
  const outgoingIdentityRef = useRef<string | number | null>(null);
  const previousStateRef = useRef<StudentContentState>(state);
  const previousCycleKeyRef = useRef<string | null>(null);
  const resetKey = explicitResetKey ?? scope?.resetKey ?? 'local';
  const previousResetKeyRef = useRef(resetKey);
  const resetVersionRef = useRef(0);
  const previousResetVersionRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const revealTimerRef = useRef<number | null>(null);
  const heightTimerRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);
  const skeletonVisibleRef = useRef(false);
  const [heldHeight, setHeldHeight] = useState<number | null>(null);
  const heldHeightRef = useRef<number | null>(null);
  const [takingLonger, setTakingLonger] = useState(false);

  const initialCycleRef = useRef({
    cycleKey: explicitCycleKey ?? scope?.cycleKey ?? localCycleKey,
    startedAt:
      cycleStartedAt ??
      (state === 'pending' ? scope?.startedAt : undefined) ??
      Date.now(),
  });
  const [activeCycle, setActiveCycle] = useState(initialCycleRef.current);

  const initialElapsed =
    state === 'pending'
      ? Math.max(0, Date.now() - initialCycleRef.current.startedAt)
      : 0;
  const initialSkeletonProgress =
    state === 'pending' && initialElapsed >= loadingDelayMs
      ? clamp(
          (initialElapsed - loadingDelayMs) / Math.max(skeletonFadeMs, 1),
          0,
          1
        )
      : 0;
  const [presentation, setPresentation] = useState<PresentationState>(() => {
    if (state === 'error') return errorPresentation;
    if (state === 'ready') return readyPresentation;
    skeletonVisibleRef.current = initialSkeletonProgress > 0;
    return {
      mode: 'pending',
      skeletonMounted: initialElapsed >= loadingDelayMs,
      skeletonOpacity: reducedMotion
        ? initialElapsed >= loadingDelayMs
          ? 1
          : 0
        : initialSkeletonProgress,
      previousOpacity:
        retainPrevious && lastReadyContentRef.current
          ? reducedMotion && initialElapsed >= loadingDelayMs
            ? 0
            : 1 - initialSkeletonProgress
          : 0,
      incomingOpacity: 0,
      transitionMs: 0,
    };
  });

  if (previousResetKeyRef.current !== resetKey) {
    previousResetKeyRef.current = resetKey;
    resetVersionRef.current += 1;
    lastReadyContentRef.current = null;
    lastReadyIdentityRef.current = null;
    lastReadyHeightRef.current = null;
    outgoingContentRef.current = null;
    outgoingIdentityRef.current = null;
  }

  const clearScheduledWork = useCallback(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    if (revealTimerRef.current !== null)
      window.clearTimeout(revealTimerRef.current);
    if (heightTimerRef.current !== null)
      window.clearTimeout(heightTimerRef.current);
    if (frameRef.current !== null)
      window.cancelAnimationFrame(frameRef.current);
    timerRef.current = null;
    revealTimerRef.current = null;
    heightTimerRef.current = null;
    frameRef.current = null;
  }, []);

  const showSkeleton = useCallback(
    (elapsedIntoFade = 0) => {
      const progress = reducedMotion
        ? 1
        : clamp(elapsedIntoFade / Math.max(skeletonFadeMs, 1), 0, 1);
      const remaining = reducedMotion
        ? 0
        : Math.max(0, skeletonFadeMs - elapsedIntoFade);
      skeletonVisibleRef.current = progress > 0;
      setPresentation({
        mode: 'pending',
        skeletonMounted: true,
        skeletonOpacity: progress,
        previousOpacity:
          retainPrevious && outgoingContentRef.current ? 1 - progress : 0,
        incomingOpacity: 0,
        transitionMs: remaining,
      });
      if (progress < 1) {
        frameRef.current = window.requestAnimationFrame(() => {
          frameRef.current = null;
          skeletonVisibleRef.current = true;
          setPresentation(current =>
            current.mode === 'pending'
              ? {
                  ...current,
                  skeletonOpacity: 1,
                  previousOpacity: 0,
                  transitionMs: remaining,
                }
              : current
          );
        });
      }
    },
    [reducedMotion, retainPrevious, skeletonFadeMs]
  );

  useLayoutEffect(() => {
    const previousState = previousStateRef.current;
    const resolvedCycleKey =
      explicitCycleKey ?? scope?.cycleKey ?? localCycleKey;
    const resetChanged =
      previousResetVersionRef.current !== resetVersionRef.current;
    const cycleChanged =
      previousCycleKeyRef.current !== resolvedCycleKey || resetChanged;

    if (state === 'pending') {
      const enteringPending = previousState !== 'pending' || cycleChanged;
      if (enteringPending) {
        clearScheduledWork();
        const mayUseScopeStart =
          scope !== null &&
          ((previousState === 'pending' &&
            previousCycleKeyRef.current === null) ||
            resetChanged ||
            (previousCycleKeyRef.current !== null &&
              previousCycleKeyRef.current !== scope.cycleKey));
        const startedAt =
          cycleStartedAt ??
          (mayUseScopeStart ? scope?.startedAt : undefined) ??
          Date.now();
        setActiveCycle({ cycleKey: resolvedCycleKey, startedAt });
        previousCycleKeyRef.current = resolvedCycleKey;
        outgoingContentRef.current = retainPrevious
          ? lastReadyContentRef.current
          : null;
        outgoingIdentityRef.current = outgoingContentRef.current
          ? lastReadyIdentityRef.current
          : null;
        const previousHeight = outgoingContentRef.current
          ? lastReadyHeightRef.current
          : null;
        heldHeightRef.current = previousHeight;
        setHeldHeight(previousHeight);
        setTakingLonger(false);
        skeletonVisibleRef.current = false;

        const elapsed = Math.max(0, Date.now() - startedAt);
        const previousOpacity = outgoingContentRef.current ? 1 : 0;
        setPresentation({
          mode: 'pending',
          skeletonMounted: elapsed >= loadingDelayMs,
          skeletonOpacity: 0,
          previousOpacity,
          incomingOpacity: 0,
          transitionMs: 0,
        });

        if (elapsed >= loadingDelayMs) {
          showSkeleton(elapsed - loadingDelayMs);
        } else {
          timerRef.current = window.setTimeout(() => {
            timerRef.current = null;
            showSkeleton();
          }, loadingDelayMs - elapsed);
        }

        if (elapsed >= longWaitMs) {
          setTakingLonger(true);
        } else {
          revealTimerRef.current = window.setTimeout(() => {
            revealTimerRef.current = null;
            setTakingLonger(true);
          }, longWaitMs - elapsed);
        }
      }
    } else if (state === 'error') {
      clearScheduledWork();
      previousCycleKeyRef.current = null;
      outgoingContentRef.current = null;
      outgoingIdentityRef.current = null;
      skeletonVisibleRef.current = false;
      setTakingLonger(false);
      heldHeightRef.current = null;
      setHeldHeight(null);
      setPresentation(errorPresentation);
    } else if (previousState === 'pending') {
      clearScheduledWork();
      previousCycleKeyRef.current = null;
      setTakingLonger(false);
      const destinationChanged = lastReadyIdentityRef.current !== contentKey;
      if (
        reducedMotion ||
        parentSlot.nested ||
        revealMs === 0 ||
        !destinationChanged
      ) {
        outgoingContentRef.current = null;
        outgoingIdentityRef.current = null;
        skeletonVisibleRef.current = false;
        heldHeightRef.current = null;
        setHeldHeight(null);
        setPresentation(readyPresentation);
      } else {
        const hadSkeleton = skeletonVisibleRef.current;
        setPresentation({
          mode: 'revealing',
          skeletonMounted: hadSkeleton,
          skeletonOpacity: hadSkeleton ? 1 : 0,
          previousOpacity: !hadSkeleton && outgoingContentRef.current ? 1 : 0,
          incomingOpacity: animateContent ? 0 : 1,
          transitionMs: revealMs,
        });

        const nextHeight = measuredHeight(incomingRef.current);
        frameRef.current = window.requestAnimationFrame(() => {
          frameRef.current = null;
          setPresentation(current =>
            current.mode === 'revealing'
              ? {
                  ...current,
                  skeletonOpacity: 0,
                  previousOpacity: 0,
                  incomingOpacity: 1,
                }
              : current
          );
          if (heldHeightRef.current !== null && nextHeight !== null) {
            heldHeightRef.current = nextHeight;
            setHeldHeight(nextHeight);
          }
        });

        revealTimerRef.current = window.setTimeout(() => {
          revealTimerRef.current = null;
          outgoingContentRef.current = null;
          outgoingIdentityRef.current = null;
          skeletonVisibleRef.current = false;
          setPresentation(readyPresentation);
        }, revealMs);
        if (heldHeightRef.current !== null) {
          heightTimerRef.current = window.setTimeout(() => {
            heightTimerRef.current = null;
            heldHeightRef.current = null;
            setHeldHeight(null);
          }, revealMs);
        }
      }
    } else {
      previousCycleKeyRef.current = null;
      outgoingContentRef.current = null;
      outgoingIdentityRef.current = null;
      setPresentation(readyPresentation);
      setTakingLonger(false);
      heldHeightRef.current = null;
      setHeldHeight(null);
    }

    previousStateRef.current = state;
    previousResetVersionRef.current = resetVersionRef.current;
  }, [
    state,
    contentKey,
    explicitCycleKey,
    cycleStartedAt,
    resetKey,
    scope,
    localCycleKey,
    retainPrevious,
    reducedMotion,
    parentSlot.nested,
    animateContent,
    loadingDelayMs,
    longWaitMs,
    revealMs,
    clearScheduledWork,
    showSkeleton,
  ]);

  useLayoutEffect(() => {
    if (state !== 'ready') return;
    lastReadyContentRef.current = children;
    lastReadyIdentityRef.current = contentKey;
    lastReadyHeightRef.current = measuredHeight(incomingRef.current);
  }, [state, contentKey, children]);

  useEffect(() => clearScheduledWork, [clearScheduledWork]);

  useEffect(() => {
    if (state !== 'pending' || parentSlot.nested || !registry) return undefined;
    registry.register({
      ...activeCycle,
      participantId,
      label,
    });
    return () => registry.unregister(activeCycle.cycleKey, participantId);
  }, [state, parentSlot.nested, registry, activeCycle, participantId, label]);

  const isPending = state === 'pending';
  const isRevealing = presentation.mode === 'revealing';
  const enteringPendingRender =
    isPending && previousStateRef.current !== 'pending';
  const renderedOutgoingContent = enteringPendingRender
    ? retainPrevious
      ? lastReadyContentRef.current
      : null
    : outgoingContentRef.current;
  const renderedOutgoingIdentity = enteringPendingRender
    ? lastReadyIdentityRef.current
    : outgoingIdentityRef.current;
  const hasPrevious = Boolean(renderedOutgoingContent);
  const destinationChanged = lastReadyIdentityRef.current !== contentKey;
  const canCrossfadePrevious =
    (destinationChanged || isRevealing) &&
    animateContent &&
    !reducedMotion &&
    !parentSlot.nested &&
    revealMs > 0 &&
    !presentation.skeletonMounted;
  const showOutgoingPrevious =
    hasPrevious &&
    (isPending ||
      (state === 'ready' &&
        canCrossfadePrevious &&
        (presentation.mode === 'pending' || isRevealing)));
  const transition =
    presentation.transitionMs > 0
      ? `opacity ${presentation.transitionMs}ms var(--ease-default)`
      : 'none';
  const heightTransition =
    heldHeight !== null && !reducedMotion && !parentSlot.nested
      ? `height ${revealMs}ms var(--ease-default)`
      : 'none';
  const inertProps = {
    inert: '',
  } as unknown as React.HTMLAttributes<HTMLDivElement>;
  const ownsLocalAnnouncement =
    isPending && !parentSlot.nested && registry === null;
  const slotValue = useMemo<StudentLoadingSlotContextValue>(
    () => ({
      nested: parentSlot.nested || isPending || isRevealing,
      startedAt: activeCycle.startedAt,
      takingLonger,
    }),
    [
      parentSlot.nested,
      isPending,
      isRevealing,
      activeCycle.startedAt,
      takingLonger,
    ]
  );

  return (
    <Box
      {...boxProps}
      ref={rootRef}
      data-student-content-state={state}
      data-student-content-key={String(contentKey)}
      role={ownsLocalAnnouncement ? 'status' : undefined}
      aria-live={ownsLocalAnnouncement ? 'polite' : undefined}
      aria-label={ownsLocalAnnouncement ? label : undefined}
      aria-busy={isPending ? 'true' : undefined}
      sx={{
        position: 'relative',
        minWidth: 0,
        ...(heldHeight !== null && { height: `${heldHeight}px` }),
        overflow: heldHeight !== null ? 'hidden' : 'visible',
        transition: heightTransition,
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        ...sx,
      }}
    >
      <StudentLoadingSlotContext.Provider value={slotValue}>
        {state === 'error' ? error : null}

        {showOutgoingPrevious ? (
          <Box
            key={`content:${String(renderedOutgoingIdentity)}`}
            {...inertProps}
            aria-hidden="true"
            data-student-content-slot={
              isPending ? 'previous' : 'outgoing-previous'
            }
            onClickCapture={blockRetainedInteraction}
            onKeyDownCapture={blockRetainedInteraction}
            onPointerDownCapture={blockRetainedInteraction}
            onSubmitCapture={blockRetainedInteraction}
            onFocusCapture={rejectRetainedFocus}
            sx={{
              position: isPending ? 'relative' : 'absolute',
              inset: isPending ? undefined : 0,
              zIndex: 1,
              pointerEvents: 'none',
              userSelect: 'none',
              opacity:
                state === 'ready' && presentation.mode === 'pending'
                  ? 1
                  : presentation.previousOpacity,
              transition,
            }}
          >
            {renderedOutgoingContent}
          </Box>
        ) : null}

        {state === 'ready' ? (
          <Box
            key={`content:${String(contentKey)}`}
            ref={incomingRef}
            data-student-content-slot="content"
            sx={{
              position: 'relative',
              // The slots' 1, 2, 3 order them among themselves, but the root
              // makes no stacking context, so they also outrank anything on
              // the page at or below them: sticky page chrome must sit above.
              zIndex: 2,
              pointerEvents: 'auto',
              userSelect: 'auto',
              opacity:
                previousStateRef.current === 'pending' &&
                presentation.mode === 'pending'
                  ? canCrossfadePrevious
                    ? 0
                    : 1
                  : presentation.incomingOpacity,
              transition,
            }}
          >
            {children}
          </Box>
        ) : null}

        {(isPending || isRevealing) && presentation.skeletonMounted ? (
          <Box
            aria-hidden="true"
            data-student-content-slot={
              isPending ? 'skeleton' : 'outgoing-skeleton'
            }
            sx={{
              position:
                hasPrevious || state === 'ready' ? 'absolute' : 'relative',
              inset: hasPrevious || state === 'ready' ? 0 : undefined,
              zIndex: 3,
              pointerEvents: 'none',
              opacity: presentation.skeletonOpacity,
              transition,
            }}
          >
            {skeleton}
          </Box>
        ) : null}

        {isPending && !hasPrevious && !presentation.skeletonMounted ? (
          <Box
            aria-hidden="true"
            data-student-content-slot="reserved"
            sx={{ visibility: 'hidden', pointerEvents: 'none' }}
          >
            {skeleton}
          </Box>
        ) : null}
      </StudentLoadingSlotContext.Provider>
    </Box>
  );
};
